import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Info, X } from 'lucide-react';
import { RouteHistoryMap } from '../../components/map/RouteHistoryMap';
import { HistoryQueryForm } from '../../components/monitoring/HistoryQueryForm';
import { HistoryPlaybackPanel } from '../../components/monitoring/HistoryPlaybackPanel';
import { Spinner } from '../../components/ui/Spinner';
import { useVehicles } from '../../hooks/useVehicles';
import { useRouteHistorySession } from '../../hooks/useRouteHistorySession';
import { NAV_ROUTES } from '../../utils/constants';

/**
 * FE-27 — histórico de recorrido en página propia, para mobile (`≤ 900px`, el
 * botón de `VehicleHistoryControls` navega aquí). Pantalla completa: cabecera
 * con flecha atrás, mapa y un panel fijo debajo — el modelo de la app de
 * referencia, en vez de un panel y una barra flotando sobre un mapa de 60vh.
 *
 * Vive FUERA de `AppShell` (ver `AppRouter`): sin topbar, `bottom-nav` ni FABs.
 *
 * La consulta viaja en la URL (`?vehicle=&from=&to=`, ISO): recargar o volver
 * con "atrás" la conserva. Se escribe con `replace`, así "atrás" sale de la
 * página en vez de recorrer cada consulta hecha.
 *
 * Estados del panel de abajo:
 *  - sin consulta / cargando / error / sin datos / un solo punto → el formulario
 *  - recorrido reproducible → el panel de reproducción (el formulario vuelve
 *    con "Consulta")
 */

/** Lee `?vehicle=&from=&to=`; devuelve `null` si falta algo o el rango no es válido. */
function parseUrlQuery(params: URLSearchParams): { vehicleId: string; from: string; to: string } | null {
  const vehicleId = params.get('vehicle');
  const from = params.get('from');
  const to = params.get('to');
  if (!vehicleId || !from || !to) return null;
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs) || toMs <= fromMs) return null;
  return { vehicleId, from, to };
}

export default function MonitoringHistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { vehicles } = useVehicles();
  const history = useRouteHistorySession();
  const { playback } = history;

  const [showStops, setShowStops] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    document.title = 'Reproducción | Paitron Radar';
  }, []);

  // La consulta de la URL se lanza UNA vez al entrar (enlace, recarga). El
  // `ref` protege del doble efecto de StrictMode en desarrollo. Los valores
  // iniciales del formulario se fijan igual, en el primer render.
  const urlQuery = useMemo(() => parseUrlQuery(searchParams), [searchParams]);
  const initialQueryRef = useRef(urlQuery);
  const autoQueriedRef = useRef(false);
  const { query: runQuery } = history;
  useEffect(() => {
    if (autoQueriedRef.current) return;
    autoQueriedRef.current = true;
    const q = initialQueryRef.current;
    if (q) runQuery(q.vehicleId, q.from, q.to);
  }, [runQuery]);

  const handleQuery = (vehicleId: string, from: string, to: string) => {
    history.query(vehicleId, from, to);
    setSearchParams({ vehicle: vehicleId, from, to }, { replace: true });
    setIsFormOpen(false);
  };

  const handleClear = () => {
    history.clear();
    setSearchParams({}, { replace: true });
    setIsFormOpen(false);
  };

  // "Atrás": si se llegó desde Monitoreo, vuelve a él; si se abrió el enlace
  // directo (`location.key === 'default'`), no hay a dónde volver → Monitoreo.
  const handleBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate(NAV_ROUTES.MONITORING, { replace: true });
  };

  const handleRestart = () => {
    playback.seek(playback.startMs);
    playback.play();
  };

  const vehicleId = searchParams.get('vehicle');
  const vehicle = vehicles.find(v => v.id === vehicleId);
  const vehicleLabel = vehicle ? vehicle.plate : null;

  const canShowPanel = !!history.route && playback.canPlay && !history.error;
  const showForm = isFormOpen || !canShowPanel;
  const onePointOnly = !!history.route && history.route.points.length === 1;

  return (
    <div className="history-page">
      <header className="history-page__header">
        <button className="history-page__back" onClick={handleBack} aria-label="Volver a Monitoreo">
          <ArrowLeft size={20} />
        </button>
        <h1 className="history-page__title">Reproducción</h1>
        {vehicleLabel && <span className="history-page__plate">{vehicleLabel}</span>}
      </header>

      <div className="history-page__map">
        <RouteHistoryMap
          route={history.route}
          playbackPosition={history.position}
          stops={showStops ? history.stops : []}
          following={history.following}
          onStopFollowing={history.stopFollowing}
        />
        {history.isLoading && (
          <div className="history-page__loading">
            <Spinner size="md" />
            <span>Cargando recorrido…</span>
          </div>
        )}
      </div>

      {/* El formulario se mantiene MONTADO y se oculta con `display: none`:
          guarda lo tecleado y "Consulta" no lo borra (mismo patrón que el
          panel flotante de desktop). */}
      <section className="history-sheet" style={showForm ? undefined : { display: 'none' }}>
        <div className="history-sheet__header">
          <h2 className="history-sheet__title">Consultar histórico</h2>
          {canShowPanel && (
            <button className="history-sheet__close" onClick={() => setIsFormOpen(false)} aria-label="Cerrar consulta">
              <X size={18} />
            </button>
          )}
        </div>
        <div className="history-sheet__body">
          <HistoryQueryForm
            vehicles={vehicles}
            route={history.route}
            isLoading={history.isLoading}
            error={history.error}
            hasActiveQuery={history.hasQuery}
            onQuery={handleQuery}
            onClear={handleClear}
            initial={initialQueryRef.current ?? undefined}
          />
          {onePointOnly && (
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Info size={12} /> El vehículo reportó una sola posición en ese rango: no hay recorrido que reproducir.
            </p>
          )}
        </div>
      </section>

      {canShowPanel && !showForm && (
        <HistoryPlaybackPanel
          playback={playback}
          position={history.position}
          distanceKm={history.distanceKm}
          downsampled={!!history.route?.downsampled}
          stopsCount={history.stops.length}
          showStops={showStops}
          vehicleLabel={vehicleLabel}
          onToggleStops={() => setShowStops(v => !v)}
          isFollowing={history.following}
          onToggleFollow={history.toggleFollow}
          onRestart={handleRestart}
          onEditQuery={() => setIsFormOpen(true)}
        />
      )}
    </div>
  );
}
